import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCircle, AlertTriangle, DollarSign, FileText, 
  X, Clock, Trash2, Check, ArrowRight, UserPlus, LogIn, MessageCircle
} from 'lucide-react';
import { notificationsAPI } from '../services/api';
import { walkInApi } from '../services/accessApi';
import { surfaceNewWalkInRequests } from '../services/walkInModal';
import { useAuth } from '../contexts/AuthContext';

// The API uses { success, data }; always normalize list payloads before render.
const asNotificationList = (payload: unknown): Notification[] => {
  if (Array.isArray(payload)) return payload as Notification[];
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    return Array.isArray(data) ? data as Notification[] : [];
  }
  return [];
};

// Message notifications (2026-09-18): backend 'message' rows arrive with the
// raw contract { id, type, title, body, url, read, created_at } while legacy
// rows use message/timestamp/actionUrl. Normalize both to the FE shape so the
// bell dropdown, unread badge and click-to-navigate work for either payload.
export const normalizeNotification = (raw: any): Notification => ({
  ...raw,
  type: raw?.type ?? 'system',
  message: raw?.message ?? raw?.body ?? '',
  timestamp: raw?.timestamp ?? raw?.created_at ?? raw?.createdAt ?? new Date().toISOString(),
  read: Boolean(raw?.read),
  actionUrl: raw?.actionUrl ?? raw?.url ?? undefined,
});

export interface Notification {
  id: string;
  type: 'expense_approval' | 'project_approval' | 'payment_received' | 'monthly_closure' | 'announcement' | 'system' | 'walk_in_request' | 'walk_in_decision' | 'message';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  // Walk-in notifications carry { access_code_id, code, visitor_name, unit_id }
  // in `data` (may arrive JSON-encoded depending on the DB driver).
  data?: Record<string, any> | string | null;
}

// UX-1 (2026-09-16): extract the walk-in access-code id from the payload.
export const getWalkInId = (notification: Notification): string | null => {
  const raw = (notification as any)?.data;
  if (!raw) return null;
  let d: any = raw;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return null; }
  }
  const id = d?.access_code_id || d?.id || null;
  return id ? String(id) : null;
};

// UX-1: walk-in rows are actionable — requests go to the host approval card in
// MyQR; decisions (guard) go to VerifyQR. Other types keep existing behavior.
export const walkInTarget = (notification: Notification): string | null => {
  if (notification.type === 'walk_in_request') return '/home/my-qr';
  if (notification.type === 'walk_in_decision') return '/verify';
  return null;
};

// UX-3b (2026-09-16): inline Aprobar/Rechazar buttons directly on pending
// walk_in_request cards — both the header dropdown and the full /notifications
// page. Reuses the EXACT endpoints the /my-qr approval card uses
// (walkInApi.approve/deny → POST /api/walk-ins/:id/approve|deny, where :id is
// the access_codes.id carried in the notification `data`). No alert() dialogs:
// button state flips to Aprobado/Rechazado, stale/failed decisions show small
// inline error text and refresh the badge/list.
type WalkInDecisionState = 'idle' | 'busy' | 'approved' | 'denied' | 'stale' | 'error';

export function WalkInDecisionButtons({ notification, onDecided }: {
  notification: Notification;
  onDecided?: (decision: 'approved' | 'denied') => void;
}) {
  const [state, setState] = useState<WalkInDecisionState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const walkInId = getWalkInId(notification);

  // Notifications are only created while the request is pending, but the
  // server remains the source of truth: a stale decision (another unit member
  // already answered, or the 15-min timeout fired) surfaces inline instead of
  // silently failing.
  const decide = async (action: 'approve' | 'deny') => {
    if (!walkInId || state === 'busy' || state === 'approved' || state === 'denied') return;
    setState('busy');
    setErrMsg(null);
    try {
      await (action === 'approve' ? walkInApi.approve(walkInId) : walkInApi.deny(walkInId));
      setState(action === 'approve' ? 'approved' : 'denied');
      // The decide endpoints already mark the original notification read for
      // all residents server-side; keep this row consistent + refresh badge.
      try { await notificationsAPI.markAsRead(notification.id); } catch { /* cosmetic */ }
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
      onDecided?.(action === 'approve' ? 'approved' : 'denied');
    } catch (err: any) {
      const msg = String(err?.response?.data?.error || err?.response?.data?.message || '');
      const stale = /already|expired|expirad|timeout|cancel/i.test(msg)
        || [400, 404, 409].includes(err?.response?.status);
      if (stale) {
        setState('stale');
        setErrMsg(msg || 'Esta solicitud ya fue decidida o expiró.');
      } else {
        setState('idle');
        setErrMsg(msg || 'No se pudo procesar la solicitud. Intenta de nuevo.');
      }
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
    }
  };

  if (!walkInId) return null;

  if (state === 'approved' || state === 'denied') {
    return (
      <span className={`text-xs font-bold px-2 py-1 rounded ${state === 'approved' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300' : 'bg-red-500/20 text-red-600 dark:text-red-300'}`}>
        {state === 'approved' ? 'Aprobado' : 'Rechazado'}
      </span>
    );
  }

  if (state === 'stale') {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400 italic">{errMsg}</span>
    );
  }

  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => decide('approve')}
        disabled={state === 'busy'}
        className="text-xs font-bold px-2.5 py-1 rounded bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white transition-colors"
      >
        {state === 'busy' ? '…' : 'Aprobar'}
      </button>
      <button
        type="button"
        onClick={() => decide('deny')}
        disabled={state === 'busy'}
        className="text-xs font-bold px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white transition-colors"
      >
        Rechazar
      </button>
      {errMsg && state === 'error' && (
        <span className="text-xs text-red-500">{errMsg}</span>
      )}
    </div>
  );
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'walk_in_request':
      return <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center"><UserPlus className="w-5 h-5 text-cyan-600" /></div>;
    case 'walk_in_decision':
      return <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><LogIn className="w-5 h-5 text-emerald-600" /></div>;
    case 'expense_approval':
    case 'project_approval':
      return <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>;
    case 'payment_received':
      return <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-600" /></div>;
    case 'monthly_closure':
      return <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div>;
    case 'announcement':
      return <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><FileText className="w-5 h-5 text-purple-600" /></div>;
    case 'message':
      return <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center"><MessageCircle className="w-5 h-5 text-teal-600" /></div>;
    default:
      return <div className="w-10 h-10 bg-[var(--v-surface-2)] rounded-xl flex items-center justify-center"><Bell className="w-5 h-5 text-gray-600" /></div>;
  }
};

const formatTimeAgo = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'hace un momento';
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)} h`;
  if (seconds < 604800) return `hace ${Math.floor(seconds / 86400)} d`;
  return date.toLocaleDateString('es-MX');
};

interface NotificationsDropdownProps {
  onClose: () => void;
}

export function NotificationsDropdown({ onClose }: NotificationsDropdownProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    loadNotifications();
  }, [user?.id]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      if (!user?.id) {
        setNotifications([]);
        return;
      }
      const response = await notificationsAPI.getAll(user.id);
      const items = asNotificationList(response?.data).map(normalizeNotification);
      setNotifications(items);
      // Feed the app-level walk-in modal store (time-sensitive pop-up).
      surfaceNewWalkInRequests(items);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  
  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    return true;
  });

  // UX-2: header badge listens for this event so mark-as-read live-updates it.
  const notifyBadge = () => window.dispatchEvent(new Event('vecindus:notifications-updated'));

  const markAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      notifyBadge();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (!user?.id) return;
      await notificationsAPI.markAllAsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      notifyBadge();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    // B3 fix (2026-10-19): actually delete server-side (per-user row), not just
    // locally — the row used to reappear on the next poll.
    notificationsAPI.deleteNotification(id).catch(err => console.error('Error deleting notification:', err));
  };

  const handleAction = (notification: Notification) => {
    markAsRead(notification.id);
    const target = walkInTarget(notification) || notification.actionUrl;
    if (target) {
      navigate(target);
      onClose();
    }
  };

  // UX-1: whole-row click for walk-in notifications (falls back to actionUrl).
  // Row click navigates for any notification carrying a target (message
  // notifications deep-link to /mensajes?conv=... via normalized actionUrl).
  const handleRowClick = (notification: Notification) => {
    const target = walkInTarget(notification) || notification.actionUrl;
    if (target) handleAction(notification);
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl border border-[var(--v-border-light)] dark:border-[var(--v-surface-3)] overflow-hidden z-50">
      <div className="p-4 border-b border-[var(--v-border-light)] flex items-center justify-between">
        <div>
          <h3 className="font-bold text-[#2D3748]">Notificaciones</h3>
          <p className="text-xs text-gray-500">{unreadCount} sin leer</p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-xs text-[#36CCD8] hover:underline"
            >
              Marcar todo leído
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-[var(--v-surface-2)] rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex border-b border-[var(--v-border-light)]">
        <button 
          onClick={() => setFilter('all')}
          className={`flex-1 py-2 text-sm font-medium ${filter === 'all' ? 'text-[#36CCD8] border-b-2 border-[#36CCD8]' : 'text-gray-500'}`}
        >
          Todas
        </button>
        <button 
          onClick={() => setFilter('unread')}
          className={`flex-1 py-2 text-sm font-medium ${filter === 'unread' ? 'text-[#36CCD8] border-b-2 border-[#36CCD8]' : 'text-gray-500'}`}
        >
          Sin leer {unreadCount > 0 && `(${unreadCount})`}
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No hay notificaciones</p>
          </div>
        ) : (
          filteredNotifications.map(notification => (
            <div 
              key={notification.id}
              onClick={() => handleRowClick(notification)}
              className={`p-4 border-b border-[var(--v-border-light)] hover:bg-[var(--v-surface-2)] transition-colors ${!notification.read ? 'bg-blue-50/50' : ''} ${walkInTarget(notification) || notification.actionUrl ? 'cursor-pointer' : ''}`}
            >
              <div className="flex gap-3">
                {getNotificationIcon(notification.type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={`font-bold text-sm ${!notification.read ? 'text-[#2D3748]' : 'text-gray-700'}`}>
                      {notification.title}
                    </h4>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatTimeAgo(notification.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                  
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {notification.type === 'walk_in_request' && (
                      <WalkInDecisionButtons
                        notification={notification}
                        onDecided={() => {
                          setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
                          notifyBadge();
                        }}
                      />
                    )}
                    {(walkInTarget(notification) || notification.actionUrl) && notification.type !== 'walk_in_request' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleAction(notification); }}
                        className="text-xs font-bold text-[#36CCD8] hover:underline flex items-center gap-1"
                      >
                        {walkInTarget(notification) ? 'Ver resultado' : (notification.actionLabel || 'Ver')} <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                    {!notification.read && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Marcar leído
                      </button>
                    )}
                    <button 
                      onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                      className="text-xs text-gray-400 hover:text-red-500 ml-auto"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="p-3 border-t border-[var(--v-border-light)] bg-[var(--v-surface-2)]">
        <button 
          onClick={() => { navigate('/home/notifications'); onClose(); }}
          className="w-full py-2 text-sm font-medium text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl transition-colors"
        >
          Ver todas las notificaciones
        </button>
      </div>
    </div>
  );
}

// Bell icon with badge for header
interface NotificationBellProps {
  onClick: () => void;
  unreadCount: number;
}

export function NotificationBell({ onClick, unreadCount }: NotificationBellProps) {
  return (
    <button 
      onClick={onClick}
      className="relative p-2 text-gray-600 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl transition-colors"
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      )}
    </button>
  );
}

// Full notifications page
export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'expense' | 'project'>('all');

  useEffect(() => {
    if (!user?.id) return;
    notificationsAPI.getAll(user.id)
      .then(response => {
        const payload = response?.data;
        const items = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        setNotifications(items.map(normalizeNotification));
        // Feed the app-level walk-in modal store (time-sensitive pop-up).
        surfaceNewWalkInRequests(items);
      })
      .catch(() => setNotifications([]));
  }, [user?.id]);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'expense') return n.type === 'expense_approval';
    if (filter === 'project') return n.type === 'project_approval';
    return true;
  });

  const markAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (!user?.id) return;
      await notificationsAPI.markAllAsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] dark:border-[var(--v-surface-3)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] dark:text-slate-200 hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowRight className="w-5 h-5 rotate-180" />
            </button>
            <h1 className="text-xl font-bold text-[#2D3748]">Centro de Notificaciones</h1>
          </div>
          <button 
            onClick={markAllAsRead}
            className="text-sm font-medium text-[#36CCD8] hover:underline"
          >
            Marcar todo como leído
          </button>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'Todas' },
            { id: 'unread', label: 'Sin leer' },
            { id: 'expense', label: 'Gastos' },
            { id: 'project', label: 'Proyectos' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id as any)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                filter === f.id
                  ? 'bg-[#36CCD8] text-white'
                  : 'bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] text-gray-600 dark:text-slate-300 hover:bg-[var(--v-surface-2)] dark:hover:bg-slate-700 border border-[var(--v-border-light)] dark:border-slate-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-3">
          {filteredNotifications.map(notification => (
            <div 
              key={notification.id}
              onClick={() => {
                const target = walkInTarget(notification) || notification.actionUrl;
                if (target) { markAsRead(notification.id); navigate(target); }
              }}
              className={`bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-2xl p-5 border transition-all cursor-pointer hover:shadow-md ${
                !notification.read ? 'border-[#36CCD8]/30 bg-blue-50/30' : 'border-[var(--v-border-light)]'
              }`}
            >
              <div className="flex gap-4">
                {getNotificationIcon(notification.type)}
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className={`font-bold ${!notification.read ? 'text-[#2D3748]' : 'text-gray-700'}`}>
                        {notification.title}
                      </h3>
                      <p className="text-gray-600 mt-1">{notification.message}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatTimeAgo(notification.timestamp)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    {!notification.read && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                        className="text-sm font-medium text-[#36CCD8] hover:underline"
                      >
                        Marcar como leída
                      </button>
                    )}
                    {notification.type === 'walk_in_request' && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <WalkInDecisionButtons
                          notification={notification}
                          onDecided={() => {
                            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
                            window.dispatchEvent(new Event('vecindus:notifications-updated'));
                          }}
                        />
                      </div>
                    )}
                    {walkInTarget(notification) && notification.type !== 'walk_in_request' && (
                      <span className="text-sm font-bold text-[#36CCD8] flex items-center gap-1">
                        Ver resultado <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                    {notification.actionUrl && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        Click para {notification.actionLabel || 'ver'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
